/**
 * Opening a link that leaves AfroYap.
 *
 * WHY window.open AND NOT @capacitor/browser
 * ──────────────────────────────────────────
 * The Store leave/return flow detects the user's return with the Capacitor App
 * plugin's `appStateChange` event, which fires when the app is genuinely
 * backgrounded. `window.open(url, '_blank')` in Capacitor's WKWebView hands the
 * URL to the system browser, which backgrounds the app — so appStateChange
 * fires on the way out and again on the way back. @capacitor/browser's
 * `Browser.open()` presents an SFSafariViewController *inside* the app, which
 * does not reliably background it; return detection there needs the plugin's own
 * `browserFinished` event instead. That plugin is also not installed (see
 * node_modules/@capacitor), and adding a native plugin means a `cap sync` plus a
 * pod install before anything works.
 *
 * So this reuses the pattern every other outbound link in the app already uses
 * (SocialLinks, FormattedText, the push-notification external-link handler).
 * If @capacitor/browser is ever added, the return detector in
 * usePurchaseReturn.ts is the ONE place that has to change with it.
 *
 * No `features` argument, deliberately: passing one (even "noopener,noreferrer")
 * makes browsers treat the call as a popup-window request, which popup blockers
 * kill. And no window.location.href fallback: navigating the current tab would
 * yank the user out of AfroYap entirely, which is worse than the link not
 * opening.
 */

/** Opens `url` in a new tab / the system browser. Returns false if it was refused. */
export function openExternalUrl(url: string): boolean {
  if (!url) return false;
  try {
    const opened = window.open(url, '_blank');
    // A popup blocker returns null. Report it so the caller can say something
    // rather than leaving the user tapping a button that appears to do nothing.
    return opened !== null;
  } catch (err) {
    console.warn('[openExternal] window.open failed', err);
    return false;
  }
}
